<template>
  <component
    :is="activeComponent"
    class="tw-media"
    :tag-name="tagName"
    :value="value"
    :style="style"
    :attr="attr"
    :element="element"
    :is-editor-mode="isEditorMode"
    :sizes="sizes"
  />
</template>

<script lang="ts" setup>
import { type PropType, computed, toRef } from 'vue';
import type { Element as ElementType } from '../../types/model';
import { useSpacing } from '@shared/composable/useSpacing';
import { useSizing } from '@shared/composable/useSizing';
import Image from './components/image.vue';
import Video from './components/video.vue';
import Embed from './components/embed.vue';

const props = defineProps({
  tagName: { type: String, default: 'img' },
  value: { type: String, default: '' },
  style: { type: Object, default: () => ({}) },
  attr: { type: Object as PropType<ElementType<'image'>['attr']>, default: () => ({}) },
  element: { type: Object as PropType<ElementType<'image'>>, default: () => ({}) },
  isEditorMode: { type: Boolean, default: false },
});

const element = toRef(props, 'element');
const { padding, margin } = useSpacing(element);

const { sizes } = useSizing(element, {
  sizes: {
    desktop: {
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
    },
    tablet: {
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
      autoHeight: true,
    },
    mobile: {
      width: '100%',
      height: 'auto',
      objectFit: 'contain',
      autoHeight: true,
    },
  },
});

const options = computed(() => props.element.options);

const activeComponent = computed(() => {
  if (options.value.mediaType === 'video') return Video;
  if (/^embed_/.test(options.value.mediaType || '')) return Embed;
  return Image
})
</script>

<style lang="postcss" scoped>
.tw-media {
  /* PADDING */
  --media-desktop-padding-top: v-bind('padding?.desktop?.top');
  --media-desktop-padding-left: v-bind('padding?.desktop?.left');
  --media-desktop-padding-bottom: v-bind('padding?.desktop?.bottom');
  --media-desktop-padding-right: v-bind('padding?.desktop?.right');

  --media-tablet-padding-top: v-bind('padding?.tablet?.top');
  --media-tablet-padding-left: v-bind('padding?.tablet?.left');
  --media-tablet-padding-bottom: v-bind('padding?.tablet?.bottom');
  --media-tablet-padding-right: v-bind('padding?.tablet?.right');

  --media-mobile-padding-top: v-bind('padding?.mobile?.top');
  --media-mobile-padding-left: v-bind('padding?.mobile?.left');
  --media-mobile-padding-bottom: v-bind('padding?.mobile?.bottom');
  --media-mobile-padding-right: v-bind('padding?.mobile?.right');
  
  /* MARGIN */
  --media-desktop-margin-top: v-bind('margin?.desktop?.top');
  --media-desktop-margin-left: v-bind('margin?.desktop?.left');
  --media-desktop-margin-bottom: v-bind('margin?.desktop?.bottom');
  --media-desktop-margin-right: v-bind('margin?.desktop?.right');

  --media-tablet-margin-top: v-bind('margin?.tablet?.top');
  --media-tablet-margin-left: v-bind('margin?.tablet?.left');
  --media-tablet-margin-bottom: v-bind('margin?.tablet?.bottom');
  --media-tablet-margin-right: v-bind('margin?.tablet?.right');

  --media-mobile-margin-top: v-bind('margin?.mobile?.top');
  --media-mobile-margin-left: v-bind('margin?.mobile?.left');
  --media-mobile-margin-bottom: v-bind('margin?.mobile?.bottom');
  --media-mobile-margin-right: v-bind('margin?.mobile?.right');

  padding-top: var(--media-mobile-padding-top, 0px);
  padding-left: var(--media-mobile-padding-left, 0px);
  padding-bottom: var(--media-mobile-padding-bottom, 0px);
  padding-right: var(--media-mobile-padding-right, 0px);
  
  margin-top: var(--media-mobile-margin-top, 0px);
  margin-left: var(--media-mobile-margin-left, 0px);
  margin-bottom: var(--media-mobile-margin-bottom, 0px);
  margin-right: var(--media-mobile-margin-right, 0px);
  
  @media (min-width: 768px) {
    padding-top: var(--media-tablet-padding-top, 0px);
    padding-left: var(--media-tablet-padding-left, 0px);
    padding-bottom: var(--media-tablet-padding-bottom, 0px);
    padding-right: var(--media-tablet-padding-right, 0px);
    
    margin-top: var(--media-tablet-margin-top, 0px);
    margin-left: var(--media-tablet-margin-left, 0px);
    margin-bottom: var(--media-tablet-margin-bottom, 0px);
    margin-right: var(--media-tablet-margin-right, 0px);
  }

  @media (min-width: 1024px) {
    padding-top: var(--media-desktop-padding-top, 0px);
    padding-left: var(--media-desktop-padding-left, 0px);
    padding-bottom: var(--media-desktop-padding-bottom, 0px);
    padding-right: var(--media-desktop-padding-right, 0px);
    
    margin-top: var(--media-desktop-margin-top, 0px);
    margin-left: var(--media-desktop-margin-left, 0px);
    margin-bottom: var(--media-desktop-margin-bottom, 0px);
    margin-right: var(--media-desktop-margin-right, 0px);
  }
}

.desktop {
  .tw-media {
    padding-top: var(--media-desktop-padding-top, 0px);
    padding-left: var(--media-desktop-padding-left, 0px);
    padding-bottom: var(--media-desktop-padding-bottom, 0px);
    padding-right: var(--media-desktop-padding-right, 0px);
    
    margin-top: var(--media-desktop-margin-top, 0px);
    margin-left: var(--media-desktop-margin-left, 0px);
    margin-bottom: var(--media-desktop-margin-bottom, 0px);
    margin-right: var(--media-desktop-margin-right, 0px);
  }
}

.tablet {
  .tw-media {
    padding-top: var(--media-tablet-padding-top, 0px);
    padding-left: var(--media-tablet-padding-left, 0px);
    padding-bottom: var(--media-tablet-padding-bottom, 0px);
    padding-right: var(--media-tablet-padding-right, 0px);
    
    margin-top: var(--media-tablet-margin-top, 0px);
    margin-left: var(--media-tablet-margin-left, 0px);
    margin-bottom: var(--media-tablet-margin-bottom, 0px);
    margin-right: var(--media-tablet-margin-right, 0px);
  }
}

.mobile {
  .tw-media {
    padding-top: var(--media-mobile-padding-top, 0px);
    padding-left: var(--media-mobile-padding-left, 0px);
    padding-bottom: var(--media-mobile-padding-bottom, 0px);
    padding-right: var(--media-mobile-padding-right, 0px);
    
    margin-top: var(--media-mobile-margin-top, 0px);
    margin-left: var(--media-mobile-margin-left, 0px);
    margin-bottom: var(--media-mobile-margin-bottom, 0px);
    margin-right: var(--media-mobile-margin-right, 0px);
  }
}
</style>
